/**
 * useAccountSocketEvents Hook
 *
 * Escuta eventos de atualização de conta (account:update) via Socket.io
 * e adiciona notificações para admin/supervisor no painel de notificações.
 *
 * Exemplo de payload esperado do backend:
 * {
 *   notification: {
 *     title: string,
 *     message: string,
 *     type: 'info' | 'warning' | 'error',
 *   },
 *   ...eventData
 * }
 */

import { useEffect, useCallback } from "react";
import { useSocket } from "@/contexts/SocketContext";
import { useAuth } from "@/contexts/AuthContext";
import { useNotifications } from "@/contexts/NotificationContext";

export function useAccountSocketEvents() {
  const { socket, isConnected } = useSocket();
  const { isAdmin, isSupervisor } = useAuth();
  const { addLocalNotification } = useNotifications();

  const shouldNotify = isAdmin() || isSupervisor();

  // Adiciona notificação ao painel
  const addNotification = useCallback(
    (notification, event) => {
      console.log("[AccountSocket][DEBUG] addNotification", {
        notification,
        event,
        shouldNotify,
      });
      if (!shouldNotify) {
        console.warn(
          "[AccountSocket][DEBUG] shouldNotify = false (usuário não é admin/supervisor)",
        );
        return;
      }
      // Permite notificação genérica se notification vier undefined
      let title = "Alerta de conta";
      let message = "Atualização de conta recebida.";
      let type = "info";
      let notificationType = "ACCOUNT_UPDATE";
      if (notification) {
        title = notification.title || title;
        message = notification.message || message;
        type = notification.type || type;
      } else {
        // Tenta extrair info útil do event
        if (event && event.violationType) {
          message = `Violação detectada: ${event.violationType}`;
        }
      }
      if (type === "error") notificationType = "ACCOUNT_ERROR";
      else if (type === "warning") notificationType = "ACCOUNT_WARNING";
      else if (type === "info") notificationType = "ACCOUNT_INFO";
      addLocalNotification({
        type: notificationType,
        title,
        body: message,
        ...event,
      });
      console.log("[AccountSocket][DEBUG] addLocalNotification called", {
        notificationType,
        title,
        message,
        event,
      });
    },
    [shouldNotify, addLocalNotification],
  );

  useEffect(() => {
    if (!socket || !isConnected) return;
    const handleAccountUpdate = (payload) => {
      console.log("[AccountSocket] Received account:update", payload);
      let notification = payload?.notification;
      let event = { ...payload };
      if (notification) delete event.notification;
      addNotification(notification, event);
    };
    socket.on("account:update", handleAccountUpdate);
    console.log("[AccountSocket] Listening for account:update events");
    return () => {
      socket.off("account:update", handleAccountUpdate);
      console.log("[AccountSocket] Stopped listening for account:update");
    };
  }, [socket, isConnected, addNotification]);

  return { isListening: isConnected };
}

export default useAccountSocketEvents;
